import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, Lock, ArrowRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in as super admin
    const token = localStorage.getItem('superAdminToken');
    const user = localStorage.getItem('superAdminUser');
    if (token && user) {
      navigate('/super-admin/dashboard');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/platform/super-admin/login`, {
        email,
        password
      });

      localStorage.setItem('superAdminToken', response.data.token);
      localStorage.setItem('superAdminUser', JSON.stringify(response.data.user));
      
      toast.success('Welcome to Super Admin Dashboard');
      navigate('/super-admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Left Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recruitizr
            </h1>
            <p className="text-slate-500 mt-2">Super Admin Portal</p>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-slate-900">Sign In</CardTitle>
              <CardDescription>Access the platform management dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Email Address
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="super-admin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="superadmin@company.com"
                      className="pl-10 border-slate-200 focus:border-slate-900"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      data-testid="super-admin-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 border-slate-200 focus:border-slate-900"
                      required
                    />
                  </div>
                </div>
                <Button
                  data-testid="super-admin-login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-sm"
                >
                  {loading ? 'Signing in...' : 'Sign In to Dashboard'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-slate-500 mt-6">
            Looking for tenant login? <a href="/login" className="text-slate-900 font-semibold hover:underline">Sign in here</a>
          </p>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />
        </div>
        <div className="relative z-10 text-center text-white max-w-lg">
          <Building2 className="h-16 w-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-4xl font-extrabold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Platform Control Center
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Manage tenants, monitor platform analytics, configure billing, 
            and oversee all organizations from one unified dashboard.
          </p>
          <div className="mt-8 flex justify-center gap-8 text-sm">
            <div>
              <div className="text-3xl font-bold">Multi-Tenant</div>
              <div className="text-slate-400">Architecture</div>
            </div>
            <div>
              <div className="text-3xl font-bold">Real-time</div>
              <div className="text-slate-400">Analytics</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
